import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { AddGameForm } from './eventAdmin/AddGameForm';
import { ManageGamesCard } from './eventAdmin/ManageGamesCard';
import { EventSettingsCard } from './eventAdmin/EventSettingsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EventAdminPage = () => {
  const { isAuthenticated, isLoading: authLoading, logout, user } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    app_title: '',
    footer_text: '',
    ticker_speed: 40,
    ticker_enabled: true,
    show_next_game: true,
    show_standings: true,
    show_recent_games: true,
    show_player_stats: true,
    season_year: '2025-26',
    regular_total_games: 0,
    playoff_total_games: 0,
    total_seats: 6,
    total_parking: 2,
    partners: [
      { id: 'F', name: 'Partner 1', total_cost: 0, amount_paid: 0 },
      { id: 'B', name: 'Partner 2', total_cost: 0, amount_paid: 0 }
    ],
    playoff_enabled: false,
    playoff_partners: [
      { id: 'F', amount_paid: 0 },
      { id: 'B', amount_paid: 0 }
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [ticketAssignments, setTicketAssignments] = useState({});
  const [backupMessage, setBackupMessage] = useState('');
  const [gamesMessage, setGamesMessage] = useState('');
  const [allGames, setAllGames] = useState([]);
  const [seasonRecord, setSeasonRecord] = useState({ wins: 0, losses: 0, home_wins: 0, home_losses: 0 });
  const [seasonRecordMessage, setSeasonRecordMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [newGame, setNewGame] = useState({
    season_type: 'regular',
    type: 'regular',
    date: '',
    opponent: '',
    timeCT: '7:30 PM',
    note: ''
  });
  const [editingGame, setEditingGame] = useState(null); // Track which game is being edited

  // Get event ID from URL query params first, then localStorage
  const getEventId = () => {
    // First check URL query params (for navigation from SuperAdmin)
    const urlParams = new URLSearchParams(window.location.search);
    const urlEventId = urlParams.get('event');
    if (urlEventId) {
      return urlEventId;
    }
    
    // Fall back to localStorage
    try {
      const eventStr = localStorage.getItem('currentEvent');
      if (eventStr) {
        const event = JSON.parse(eventStr);
        return event.id;
      }
    } catch (e) {
      console.error('Error getting event ID:', e);
    }
    return null; // Return null instead of hardcoded fallback
  };

  const eventId = getEventId();

  // Check both AuthContext and sessionStorage for authentication
  useEffect(() => {
    if (authLoading) return;
    
    // Check if authenticated via AuthContext, session data, or SuperAdmin session
    const sessionUser = sessionStorage.getItem('currentUser');
    const sessionAuth = sessionStorage.getItem('isAuthenticated');
    const superAdminUser = sessionStorage.getItem('superadmin_user');
    
    const isLoggedIn = isAuthenticated || sessionAuth === 'true' || sessionUser || superAdminUser;
    
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    
    // Ensure we have a valid event selected
    if (!eventId) {
      alert('No event selected. Please select an event first.');
      navigate('/superadmin');
      return;
    }
    
    loadSettings();
  }, [isAuthenticated, authLoading, navigate, eventId]);

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Track changes to settings
  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    setHasUnsavedChanges(true);
  };

  const loadSettings = async () => {
    // Safety check - don't load if no event ID
    if (!eventId) {
      setLoading(false);
      return;
    }
    
    try {
      const [settingsRes, ticketsRes, gamesRes] = await Promise.all([
        axios.get(`${API}/settings/?event_id=${eventId}`),
        axios.get(`${API}/tickets/assignments?event_id=${eventId}`),
        axios.get(`${API}/games/schedule?event_id=${eventId}`)
      ]);
      
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.settings);
        setOriginalSettings(JSON.stringify(settingsRes.data.settings));
        setHasUnsavedChanges(false);
      }
      
      if (ticketsRes.data) {
        setTicketAssignments(ticketsRes.data.assignments || ticketsRes.data);
      }
      
      if (gamesRes.data.success) {
        setAllGames(gamesRes.data.games || []);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await axios.post(`${API}/settings/?event_id=${eventId}`, settings);
      if (response.data.success) {
        setMessage('✅ Settings saved successfully!');
        setOriginalSettings(JSON.stringify(settings));
        setHasUnsavedChanges(false);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('❌ Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handlePartnerChange = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      partners: prev.partners.map((partner, i) => 
        i === index ? { ...partner, [field]: value } : partner
      )
    }));
    setHasUnsavedChanges(true);
  };

  const addPartner = () => {
    if (settings.partners.length < 4) {
      setSettings(prev => ({
        ...prev,
        partners: [...prev.partners, { id: '', name: '', total_cost: 0, amount_paid: 0 }]
      }));
      setHasUnsavedChanges(true);
    }
  };

  const removePartner = (index) => {
    if (settings.partners.length > 1) {
      setSettings(prev => ({
        ...prev,
        partners: prev.partners.filter((_, i) => i !== index)
      }));
      setHasUnsavedChanges(true);
    }
  };

  // Backup functions
  const handleBackup = async () => {
    try {
      setBackupMessage('Creating backup...');
      const response = await axios.get(`${API}/backup/export`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mavericks_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setBackupMessage('✅ Backup downloaded successfully!');
      setTimeout(() => setBackupMessage(''), 5000);
    } catch (error) {
      setBackupMessage('❌ Backup failed. Please try again.');
    }
  };

  const handleRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!window.confirm('⚠️ WARNING: This will restore the database from the backup file. Continue?')) {
      event.target.value = '';
      return;
    }

    try {
      setBackupMessage('Restoring database...');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/backup/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setBackupMessage(`✅ Database restored! Collections: ${response.data.restored_collections.join(', ')}`);
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      setBackupMessage('❌ Restore failed. Please check the file format.');
    }
    event.target.value = '';
  };

  // Games Schedule Import/Export
  const handleExportGames = async () => {
    try {
      setGamesMessage('Exporting games schedule...');
      const response = await axios.get(`${API}/games/export-excel?event_id=${eventId}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `games_schedule_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setGamesMessage('✅ Games schedule exported!');
      setTimeout(() => setGamesMessage(''), 5000);
    } catch (error) {
      setGamesMessage('❌ Export failed. Please try again.');
    }
  };

  const handleImportGames = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!window.confirm('⚠️ WARNING: This will replace the current games schedule. Continue?')) {
      event.target.value = '';
      return;
    }

    try {
      setGamesMessage('Importing games schedule...');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/games/import-excel?event_id=${eventId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setGamesMessage(`✅ Imported ${response.data.games_count} games!`);
        loadSettings();
      }
    } catch (error) {
      setGamesMessage('❌ Import failed. Check file format.');
    }
    event.target.value = '';
  };

  // Add new game
  const handleAddGame = async () => {
    if (!newGame.date || !newGame.opponent) {
      setGamesMessage('❌ Please fill in Date and Opponent fields');
      return;
    }

    try {
      setGamesMessage('Adding game...');
      // Use single-game POST endpoint so we don't destructively rewrite the
      // full schedule (which was silently dropping location/end_date/etc.).
      await axios.post(`${API}/games/`, {
        season_type: newGame.season_type,
        type: newGame.type,
        date: newGame.date,
        opponent: newGame.opponent,
        timeCT: newGame.timeCT,
        note: newGame.note || ''
      }, { params: { event_id: eventId } });
      
      setGamesMessage('✅ Game added successfully!');
      setNewGame({ season_type: 'regular', type: 'regular', date: '', opponent: '', timeCT: '7:30 PM', note: '' });
      loadSettings();
      setTimeout(() => setGamesMessage(''), 5000);
    } catch (error) {
      setGamesMessage('❌ Failed to add game.');
    }
  };

  // Delete game
  const handleDeleteGame = async (gameIndex) => {
    if (!window.confirm(`Are you sure you want to delete this game?`)) return;

    try {
      setGamesMessage('Deleting game...');
      // Delete individually so other games' preserved fields (location,
      // end_date, is_perpetual, etc.) are not wiped.
      await axios.delete(`${API}/games/${gameIndex}`, { params: { event_id: eventId } });
      
      setGamesMessage('✅ Game deleted!');
      loadSettings();
      setTimeout(() => setGamesMessage(''), 3000);
    } catch (error) {
      setGamesMessage('❌ Failed to delete game.');
    }
  };

  // Start editing a game
  const handleEditGame = (game) => {
    setEditingGame({ ...game });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingGame(null);
  };

  // Save edited game
  const handleSaveEdit = async () => {
    if (!editingGame) return;

    try {
      setGamesMessage('Saving changes...');
      // PATCH only this single game so we don't wipe location, end_date,
      // endTime, is_perpetual or user_email on the rest of the schedule.
      await axios.patch(`${API}/games/${editingGame.game_index}`, {
        opponent: editingGame.opponent,
        date: editingGame.date,
        timeCT: editingGame.timeCT,
        note: editingGame.note || '',
        type: editingGame.season_type || editingGame.type || 'regular'
      }, { params: { event_id: eventId } });
      
      setGamesMessage('✅ Game updated!');
      setEditingGame(null);
      loadSettings();
      setTimeout(() => setGamesMessage(''), 3000);
    } catch (error) {
      setGamesMessage('❌ Failed to update game.');
    }
  };

  // Calculate partner usage cost - uses actual game counts from loaded games
  const calculatePartnerUsage = (partnerId) => {
    if (!ticketAssignments || Object.keys(ticketAssignments).length === 0) return 0;

    // Calculate game counts from actual loaded games
    const regularGames = allGames.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length;
    const playoffGames = allGames.filter(g => g.type === 'playoff' || g.season_type === 'playoff').length;
    const seats = settings.total_seats ?? 6;
    
    const regularCostPerTicket = regularGames > 0 && seats > 0
      ? settings.partners.reduce((sum, p) => sum + (p.amount_paid || 0), 0) / (regularGames * seats)
      : 0;

    const playoffTotalPaid = (settings.playoff_partners || []).reduce((sum, p) => sum + (p?.amount_paid || 0), 0);
    const playoffCostPerTicket = playoffTotalPaid > 0 && playoffGames > 0
      ? playoffTotalPaid / (playoffGames * seats)
      : 0;

    let regularTickets = 0;
    let playoffTickets = 0;
    
    Object.entries(ticketAssignments).forEach(([gameIdxStr, game]) => {
      if (game.tickets && Array.isArray(game.tickets)) {
        const partnerTickets = game.tickets.filter(t => t === partnerId).length;
        const gameIdx = parseInt(gameIdxStr);
        const actualGame = allGames.find(g => g.game_index === gameIdx);
        
        if (actualGame) {
          const isPlayoffGame = actualGame.season_type === 'playoff' || actualGame.type === 'playoff';
          if (isPlayoffGame) {
            playoffTickets += partnerTickets;
          } else {
            regularTickets += partnerTickets;
          }
        }
      }
    });

    return (regularTickets * regularCostPerTicket) + (playoffTickets * playoffCostPerTicket);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gradient">⚙️ Event Administration</h1>
            <p className="text-muted-foreground mt-1">Manage games, partners, and settings</p>
          </div>
          <div className="flex gap-3 items-center">
            {hasUnsavedChanges && (
              <Badge variant="destructive" className="animate-pulse">
                ⚠️ Unsaved Changes
              </Badge>
            )}
            <Button variant="outline" onClick={() => {
              // Check if came from SuperAdmin
              const fromSuperAdmin = sessionStorage.getItem('superadmin_user') !== null;
              const targetRoute = fromSuperAdmin ? '/superadmin' : '/dashboard';
              
              if (hasUnsavedChanges) {
                if (window.confirm('You have unsaved changes. Are you sure you want to leave without saving?')) {
                  navigate(targetRoute);
                }
              } else {
                navigate(targetRoute);
              }
            }}>
              ← Back to Dashboard
            </Button>
            <Button variant="outline" onClick={() => {
              if (hasUnsavedChanges) {
                if (window.confirm('You have unsaved changes. Are you sure you want to logout without saving?')) {
                  sessionStorage.removeItem('currentUser');
                  sessionStorage.removeItem('isAuthenticated');
                  logout();
                  navigate('/');
                }
              } else {
                sessionStorage.removeItem('currentUser');
                sessionStorage.removeItem('isAuthenticated');
                logout();
                navigate('/');
              }
            }}>
              Logout
            </Button>
          </div>
        </div>

        {/* Status Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('✅') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {message}
          </div>
        )}

        <div className="space-y-6">
          {/* Appearance Settings */}
          <EventSettingsCard
            settings={settings}
            allGames={allGames}
            onChange={handleChange}
          />

          {/* News Ticker Settings */}
          <Card>
            <CardHeader>
              <CardTitle>📰 News Ticker</CardTitle>
              <CardDescription>Configure the scrolling news ticker</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="ticker_enabled"
                  checked={settings.ticker_enabled || false}
                  onChange={(e) => handleChange('ticker_enabled', e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="ticker_enabled" className="text-sm font-medium">Enable Ticker</label>
              </div>

              <div>
                <label className="text-sm font-medium">Scroll Speed (seconds per loop)</label>
                <Input
                  type="number"
                  value={settings.ticker_speed || 40}
                  onChange={(e) => handleChange('ticker_speed', parseInt(e.target.value) || 40)}
                  className="mt-1"
                  min="10"
                  max="120"
                />
                <p className="text-xs text-muted-foreground mt-1">Lower = Faster, Higher = Slower (Recommended: 30-50)</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold">Show in Ticker:</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="show_next_game" checked={settings.show_next_game || false}
                      onChange={(e) => handleChange('show_next_game', e.target.checked)} className="w-4 h-4" />
                    <label htmlFor="show_next_game" className="text-sm">Next Home Game Countdown</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="show_standings" checked={settings.show_standings || false}
                      onChange={(e) => handleChange('show_standings', e.target.checked)} className="w-4 h-4" />
                    <label htmlFor="show_standings" className="text-sm">Conference Standings</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="show_recent_games" checked={settings.show_recent_games || false}
                      onChange={(e) => handleChange('show_recent_games', e.target.checked)} className="w-4 h-4" />
                    <label htmlFor="show_recent_games" className="text-sm">Recent Game Results</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="show_player_stats" checked={settings.show_player_stats || false}
                      onChange={(e) => handleChange('show_player_stats', e.target.checked)} className="w-4 h-4" />
                    <label htmlFor="show_player_stats" className="text-sm">Player Stats</label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partners & Costs */}
          <Card>
            <CardHeader>
              <CardTitle>👥 Partners & Costs</CardTitle>
              <CardDescription>
                Configure ticket-sharing partners and costs (up to 4 partners)
                <br />
                <span className="text-xs mt-1 block">
                  Regular Season cost per ticket: Total Paid ÷ (Regular Games × Seats)
                  {allGames.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length > 0 && settings.total_seats > 0 && (
                    <span className="font-semibold ml-1">
                      = ${(settings.partners.reduce((sum, p) => sum + (p.amount_paid || 0), 0) / (allGames.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length * settings.total_seats)).toFixed(2)} per ticket
                    </span>
                  )}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings.partners.map((partner, index) => (
                <div key={index} className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm">Partner {index + 1}</h4>
                    {settings.partners.length > 1 && (
                      <Button variant="outline" size="sm" onClick={() => removePartner(index)}
                        className="text-red-500 hover:text-red-600">Remove</Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-sm font-medium">Partner ID/Initial</label>
                      <Input
                        value={partner.id || ''}
                        onChange={(e) => handlePartnerChange(index, 'id', e.target.value)}
                        className="mt-1"
                        placeholder="F"
                        maxLength={3}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Full Name</label>
                      <Input
                        value={partner.name || ''}
                        onChange={(e) => handlePartnerChange(index, 'name', e.target.value)}
                        className="mt-1"
                        placeholder="Finley"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Amount Paid ($)</label>
                      <Input
                        type="number"
                        value={partner.amount_paid || 0}
                        onChange={(e) => handlePartnerChange(index, 'amount_paid', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Calculated Usage ($)</label>
                      <div className="mt-1 p-2 bg-muted rounded text-sm font-semibold">
                        ${calculatePartnerUsage(partner.id).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm bg-primary/10 p-3 rounded">
                    <span className="text-muted-foreground">Balance: </span>
                    <span className={`font-semibold ${(partner.amount_paid || 0) - calculatePartnerUsage(partner.id) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      ${Math.abs((partner.amount_paid || 0) - calculatePartnerUsage(partner.id)).toFixed(2)} 
                      {(partner.amount_paid || 0) - calculatePartnerUsage(partner.id) >= 0 ? ' (Credit)' : ' (Owes)'}
                    </span>
                  </div>
                </div>
              ))}

              {settings.partners.length < 4 && (
                <Button variant="outline" onClick={addPartner} className="w-full">+ Add Partner</Button>
              )}

              {/* Season Financial Summary */}
              <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                <h4 className="font-semibold text-sm mb-3">Season Financial Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Cost per Ticket:</span>
                    <div className="font-semibold">
                      ${allGames.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length > 0 && settings.total_seats > 0 
                        ? (settings.partners.reduce((sum, p) => sum + (p.amount_paid || 0), 0) / (allGames.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length * settings.total_seats)).toFixed(2)
                        : '0.00'}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Tickets:</span>
                    <div className="font-semibold">{allGames.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length * (settings.total_seats || 0)}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Paid:</span>
                    <div className="font-semibold">${settings.partners.reduce((sum, p) => sum + (p.amount_paid || 0), 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Usage:</span>
                    <div className="font-semibold">${settings.partners.reduce((sum, p) => sum + calculatePartnerUsage(p.id), 0).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Playoff Season */}
          <Card>
            <CardHeader>
              <CardTitle>🏆 Playoff Season</CardTitle>
              <CardDescription>Configure playoff financial contributions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="playoff_enabled"
                  checked={settings.playoff_enabled || false}
                  onChange={(e) => handleChange('playoff_enabled', e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="playoff_enabled" className="text-sm font-medium">Enable Playoff Season</label>
              </div>

              {settings.playoff_enabled && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-sm">💰 Playoff Partner Contributions</h4>
                  {settings.partners.map((partner, index) => {
                    const playoffPartner = (settings.playoff_partners || []).find(p => p?.id === partner.id) || { id: partner.id, amount_paid: 0 };
                    return (
                      <div key={index} className="border border-border rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm">{partner.name} ({partner.id})</span>
                        </div>
                        <div>
                          <label className="text-sm">Amount Paid for Playoffs ($)</label>
                          <Input
                            type="number"
                            value={playoffPartner.amount_paid || 0}
                            onChange={(e) => {
                              const newPlayoffPartners = (settings.playoff_partners || []).map(p =>
                                p?.id === partner.id ? { ...p, amount_paid: parseFloat(e.target.value) || 0 } : p
                              );
                              if (!newPlayoffPartners.some(p => p?.id === partner.id)) {
                                newPlayoffPartners.push({ id: partner.id, amount_paid: parseFloat(e.target.value) || 0 });
                              }
                              handleChange('playoff_partners', newPlayoffPartners);
                            }}
                            className="mt-1"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="p-3 bg-primary/10 rounded">
                    <p className="text-sm font-semibold">
                      Total Playoff Fund: ${(settings.playoff_partners || []).reduce((sum, p) => sum + (p?.amount_paid || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save All Settings Button */}
          <Button onClick={handleSave} disabled={saving} className="w-full" size="lg">
            {saving ? 'Saving...' : '💾 Save All Settings'}
          </Button>

          {/* Backup & Restore */}
          <Card>
            <CardHeader>
              <CardTitle>💾 Backup & Restore</CardTitle>
              <CardDescription>Export and import your database</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">📥 Export Backup</h4>
                  <p className="text-sm text-muted-foreground mb-3">Download a complete backup of all data.</p>
                  <Button onClick={handleBackup} className="w-full">Download Backup</Button>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">📤 Restore from Backup</h4>
                  <p className="text-sm text-muted-foreground mb-3">⚠️ Warning: This will replace all current data.</p>
                  <Input type="file" accept=".json" onChange={handleRestore} className="cursor-pointer" />
                </div>
              </div>
              {backupMessage && (
                <p className={`text-sm text-center ${backupMessage.includes('✅') ? 'text-green-600' : backupMessage.includes('❌') ? 'text-red-500' : 'text-blue-500'}`}>
                  {backupMessage}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Games Schedule Management */}
          <Card>
            <CardHeader>
              <CardTitle>📅 Games Schedule Management</CardTitle>
              <CardDescription>Import/Export games schedule via Excel</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">📥 Export Games to Excel</h4>
                  <p className="text-sm text-muted-foreground mb-3">Download current games schedule as Excel file.</p>
                  <Button onClick={handleExportGames} className="w-full">Export Games (.xlsx)</Button>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">📤 Import Games from Excel</h4>
                  <p className="text-sm text-muted-foreground mb-3">⚠️ Warning: This will replace the current schedule.</p>
                  <Input type="file" accept=".xlsx,.xls" onChange={handleImportGames} className="cursor-pointer" />
                </div>
              </div>
              {gamesMessage && (
                <p className={`text-sm text-center ${gamesMessage.includes('✅') ? 'text-green-600' : gamesMessage.includes('❌') ? 'text-red-500' : 'text-blue-500'}`}>
                  {gamesMessage}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Add New Game */}
          <AddGameForm
            newGame={newGame}
            setNewGame={setNewGame}
            onAddGame={handleAddGame}
          />

          {/* Manage Existing Games */}
          <ManageGamesCard
            allGames={allGames}
            editingGame={editingGame}
            setEditingGame={setEditingGame}
            onEditGame={handleEditGame}
            onCancelEdit={handleCancelEdit}
            onSaveEdit={handleSaveEdit}
            onDeleteGame={handleDeleteGame}
            formatDate={formatDate}
          />
        </div>
      </div>
    </div>
  );
};

export default EventAdminPage;
